import React, { useEffect } from "react";
import { useRouter } from "next/router";

import { ClerkProvider } from "@clerk/nextjs";
import PlausibleProvider from "next-plausible";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { SWRConfig } from "swr";

import Analytics from "@/components/analytics";
import { LoaderProvider } from "@/components/context/FullScreenLoaderContext";
import { useUser } from "@/components/hooks/useUser";
import { TooltipProvider } from "@/components/ui/tooltip";
import { isAuthRecoveryEnabled } from "@/lib/posthog/auth-recovery-flag";
import { api, formatApiError, logFrontendError } from "@/lib/utils";
import { jakartaSans } from "@/styles/fonts";

/**
 * SWR configuration shared across all routes.
 *
 * The global fetcher delegates to the axios `api` instance from @/lib/utils
 * so transient 401 recovery (Clerk session refresh + retry) is handled by
 * the same interceptor that covers every other request in the app. Per-hook
 * fetchers that wrap `handleApiRequest({ makeRequest: () => api.get(...) })`
 * already inherit the same recovery for free.
 *
 * Errors reaching `onError` here are either:
 *   • a persistent 401 (interceptor's refresh+retry both failed — user is
 *     genuinely signed out; surface the toast and let the middleware redirect
 *     on the next navigation)
 *   • a non-401 4xx/5xx
 *   • a network failure (no `.response`)
 *
 * formatApiError + logFrontendError already classify these and route them
 * to toast + (conditionally) Sentry. With the authRecoveryInterceptor flag
 * ON there is no `window.location.href` redirect for 401s — bouncing to
 * /dashboard on every transient blip is the bug we're fixing. The legacy 403
 * redirect is kept in BOTH modes (permission failures are not the
 * interceptor's concern). With the flag OFF the legacy 401 redirect is also
 * restored so the kill switch returns the app to its known pre-PR behavior.
 */
const swrConfig = {
  revalidateOnFocus: false,

  onError: (error: unknown) => {
    const formattedError = formatApiError(error);

    if (!isAuthRecoveryEnabled()) {
      // Kill switch OFF — legacy (pre-PR #1935) behavior: report every error
      // and hard-bounce to /dashboard on auth failures. The interceptor in
      // @/lib/utils is also disabled by the same flag, so this redirect is
      // the only stale-session recovery in this mode.
      logFrontendError(formattedError.message, formattedError, formattedError.message);
      if (formattedError.status === 401 || formattedError.status === 403) {
        window.location.href = "/dashboard";
      }
      return;
    }

    // A persistent 401 here means the axios interceptor in @/lib/utils already
    // exhausted refresh + retry and fired the single-fingerprint
    // auth-recovery-failed signal (Sentry + Segment) plus the terminal toast +
    // redirect. Re-capturing it via logFrontendError would re-introduce the
    // exact per-hook 401 noise that signal exists to replace.
    if (formattedError.status === 401) return;
    logFrontendError(formattedError.message, formattedError, formattedError.message);
    // 403 is a permission failure, not a stale session — the interceptor has
    // no business with it, so the legacy bounce to /dashboard stays. This PR
    // only removes the redirect for 401s (the transient-blip bug).
    if (formattedError.status === 403) {
      window.location.href = "/dashboard";
    }
  },
  fetcher: async (url: string) => {
    const response = await api.get(url);
    return response.data;
  },
};

/**
 * Identifies the current user to PostHog with their properties.
 * This enables feature flag targeting based on user attributes like email, isSuperUser, etc.
 * Only runs on dashboard routes to avoid identifying anonymous visitors.
 * Must be rendered inside ClerkProvider to access user context.
 */
function PostHogIdentify() {
  const router = useRouter();
  const { user, isLoaded, isSuperUser, isAgencyUser, isDealManager } = useUser();

  const isDashboardRoute = router.pathname.startsWith("/dashboard");

  useEffect(() => {
    if (!isDashboardRoute || !isLoaded || !user) return;

    posthog.identify(user.id, {
      email: user.primaryEmailAddress?.emailAddress,
      isSuperUser,
      isAgencyUser,
      isDealManager,
      firstName: user.firstName,
      lastName: user.lastName,
    });

    // Reload feature flags after identify to ensure they evaluate with user properties
    posthog.reloadFeatureFlags();
  }, [user, isLoaded, isSuperUser, isAgencyUser, isDealManager, isDashboardRoute]);

  return null;
}

interface ProvidersProps {
  children: React.ReactNode;
  skipClerk?: boolean;
  [key: string]: unknown;
}

export default function Providers({ children, skipClerk = false, ...pageProps }: ProvidersProps) {
  // Shared outer providers - same for all routes
  return (
    <PostHogProvider client={posthog}>
      <PlausibleProvider
        domain="rebolthq.com"
        trackOutboundLinks
        taggedEvents
        enabled={process.env.NODE_ENV === "production"}
      >
        <LoaderProvider>
          <TooltipProvider delayDuration={0}>
            <SWRConfig value={swrConfig}>
              {skipClerk ? (
                children
              ) : (
                // Full provider tree with Clerk for authenticated routes
                <ClerkProvider
                  {...pageProps}
                  telemetry={false}
                  touchSession
                  appearance={{
                    variables: { colorPrimary: "#FF5700", fontFamily: jakartaSans.style.fontFamily },
                  }}
                >
                  {children}
                  {/* analytics and posthog identify require clerk context (useUser hook) — only render when clerk is available */}
                  <PostHogIdentify />
                  <Analytics />
                </ClerkProvider>
              )}
            </SWRConfig>
          </TooltipProvider>
        </LoaderProvider>
      </PlausibleProvider>
    </PostHogProvider>
  );
}
